import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Building2, MapPin, Store as StoreIcon } from "lucide-react";

interface Node { id: string; name: string; code?: string; region_id?: string | null; district_id?: string | null; store_number?: string; iana_tz?: string; status?: string; }

export default function Hierarchy() {
  const [data, setData] = useState<{ regions: Node[]; districts: Node[]; stores: Node[] } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/org/tree").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingBlock />;
  if (!data) return null;

  return (
    <div>
      <PageHeader title="Company Hierarchy" subtitle="Regions, districts, and stores in your scope" testId="hierarchy-header" />
      <div className="space-y-4">
        {data.regions.map((region) => (
          <Card key={region.id} className="p-4" data-testid={`region-${region.id}`}>
            <div className="flex items-center gap-2 font-display font-semibold">
              <Building2 size={17} className="text-brand" /> {region.name}
              {region.code && <span className="text-xs text-muted-foreground">({region.code})</span>}
            </div>
            <div className="mt-3 space-y-3 pl-4">
              {data.districts.filter((d) => d.region_id === region.id).map((district) => (
                <div key={district.id} className="border-l-2 border-border pl-4" data-testid={`district-${district.id}`}>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <MapPin size={15} className="text-muted-foreground" /> {district.name}
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {data.stores.filter((s) => s.district_id === district.id).map((store) => (
                      <div key={store.id} data-testid={`hier-store-${store.store_number}`}
                        className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2">
                        <span className="flex items-center gap-2 text-sm">
                          <StoreIcon size={14} className="text-muted-foreground" />
                          <span className="font-medium">{store.name}</span>
                          <span className="text-xs text-muted-foreground">#{store.store_number}</span>
                        </span>
                        <StatusPill status={store.status || "active"} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
